import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { encodeQr, qrToSvgPath } from '../lib/qr';
import { roomUrl } from '../lib/router';
import { getMuted, setMuted } from '../lib/storage';
import { Sfx } from '../audio/sfx';
import {
  burnedWords,
  damageReport,
  finishingWord,
  opponent as opponentOf,
  you as youOf,
} from '../game/state';
import { useGame } from '../game/useGame';
import {
  ConnectionBanner,
  Countdown,
  HeartBreak,
  VideoTile,
  WordCards,
} from './hud';

export function Room({ code }: { code: string }) {
  const forceRelay = useMemo(
    () => new URLSearchParams(location.search).has('relay'),
    [],
  );
  const game = useGame({ code, forceRelay });
  const { state } = game;
  const snap = state.snapshot;
  const phase = game.phase;

  const me = youOf(state);
  const them = opponentOf(state);

  const sfxRef = useRef<Sfx | null>(null);
  const [muted, setMutedState] = useState(getMuted);
  if (!sfxRef.current) sfxRef.current = new Sfx({ muted });

  const toggleMute = useCallback(() => {
    setMutedState((prev) => {
      const next = !prev;
      setMuted(next);
      sfxRef.current?.setMuted(next);
      return next;
    });
  }, []);

  useEffect(() => () => sfxRef.current?.dispose(), []);

  const prevPhase = useRef(phase);
  useEffect(() => {
    const sfx = sfxRef.current;
    if (!sfx || prevPhase.current === phase) {
      prevPhase.current = phase;
      return;
    }
    if (prevPhase.current === 'waiting') sfx.play('join');
    if (phase === 'gameover') {
      const won = snap?.winnerId != null && me?.id === snap.winnerId;
      sfx.play(won ? 'victory' : 'defeat');
    }
    prevPhase.current = phase;
  }, [phase, snap?.winnerId, me?.id]);

  useEffect(() => {
    sfxRef.current?.setHeartbeat(phase === 'playing' && (me?.lives ?? 3) === 1);
  }, [phase, me?.lives]);

  const fx = state.fx;
  useEffect(() => {
    if (!fx) return;
    const sfx = sfxRef.current;
    if (!sfx) return;
    const delay = game.delayUntil(fx.showAt);
    const timer = setTimeout(() => {
      sfx.play('heart-lost');
      sfx.duck(0.35, 1200);
    }, delay);
    return () => clearTimeout(timer);
  }, [fx, game]);

  const outcomeFor = useCallback(
    (playerId: string | undefined): 'winner' | 'loser' | undefined => {
      if (phase !== 'gameover' || !playerId) return undefined;
      const winnerId = snap?.winnerId;
      if (winnerId == null) return undefined;
      return playerId === winnerId ? 'winner' : 'loser';
    },
    [phase, snap?.winnerId],
  );

  const onNotMe = useCallback(() => {
    if (fx?.word) game.reportFalsePositive(fx.word);
    game.dismissFx();
  }, [fx, game]);


  if (state.detectorError) {
    return <Fatal title="This browser can’t hear you" detail={state.detectorError} />;
  }

  return (
    <main className={`room room--${phase}`}>
      <div className="room__bg" aria-hidden="true" />

      {
}
      <ConnectionBanner
        socket={state.socket}
        connection={state.connection}
        bothPresent={(snap?.players.length ?? 0) === 2}
        onRetry={game.retryConnection}
      />

      {phase === 'waiting' && <Waiting code={code} />}

      {phase !== 'waiting' && (
        <>
          <div className="stage">
            <VideoTile
              stream={game.remoteStream}
              player={them}
              label="Them"
              danger={fx?.playerId === them?.id}
              outcome={outcomeFor(them?.id)}
              action={
                <button
                  type="button"
                  className="tile__action"
                  onClick={toggleMute}
                  aria-pressed={muted}
                  aria-label={muted ? 'Unmute sound' : 'Mute sound'}
                >
                  {muted ? '🔇' : '🔊'}
                </button>
              }
            />
            <VideoTile
              stream={game.localStream}
              player={me}
              mirrored
              self
              muted
              label="You"
              danger={fx?.playerId === me?.id}
              outcome={outcomeFor(me?.id)}
            />
          </div>

          {(phase === 'playing' || phase === 'countdown') && (
            <WordCards words={state.yourWords} burned={burnedWords(state)} />
          )}
        </>
      )}

      {
}
      {!state.hasMedia && (
        <div className="prompt">
          {state.mediaError && (
            <p className="prompt__error" role="alert">
              {state.mediaError}
            </p>
          )}
          <button type="button" className="button button--primary button--big" onClick={game.requestMedia}>
            Camera &amp; mic
          </button>
        </div>
      )}

      {phase === 'countdown' && snap?.startsAt != null && (
        <Countdown startsAt={snap.startsAt} delayUntil={game.delayUntil} />
      )}

      {phase === 'gameover' && <GameOver game={game} />}

      {fx && (
        <HeartBreak
          key={fx.key}
          word={fx.word}
          isYou={fx.playerId === me?.id}
          delayMs={game.delayUntil(fx.showAt)}
          onDone={game.dismissFx}
          onNotMe={onNotMe}
        />
      )}
    </main>
  );
}


function Waiting({ code }: { code: string }) {
  const url = roomUrl(code);
  const [copied, setCopied] = useState(false);

  const qrPath = useMemo(() => {
    try {
      const matrix = encodeQr(url);
      return { d: qrToSvgPath(matrix), size: matrix.size };
    } catch {
      return null;
    }
  }, [url]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
    }
  }

  return (
    <section className="waiting">
      <p className="waiting__hint">Share this invite with a friend to play</p>
      {qrPath && (
        <svg
          className="waiting__qr"
          viewBox={`0 0 ${qrPath.size} ${qrPath.size}`}
          role="img"
          aria-label="QR code linking to this room"
        >
          <rect width={qrPath.size} height={qrPath.size} fill="#fff" />
          <path d={qrPath.d} fill="#000" />
        </svg>
      )}
      <div className="waiting__link">
        <input className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
        <button type="button" className="button button--primary" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        {'share' in navigator && (
          <button
            type="button"
            className="button"
            onClick={() => void navigator.share({ url }).catch(() => {})}
          >
            Share
          </button>
        )}
      </div>
    </section>
  );
}


function GameOver({ game }: { game: ReturnType<typeof useGame> }) {
  const { state } = game;
  const me = youOf(state);
  const won = state.snapshot?.winnerId != null && me?.id === state.snapshot.winnerId;

  const culprit = finishingWord(state);
  const damage = damageReport(state);

  const title = won ? 'YOU WIN' : culprit ? 'DESTROYED BY' : 'YOU LOSE';

  return (
    <section className={`gameover gameover--${won ? 'win' : 'lose'}`}>
      <h2 className="gameover__title">{title}</h2>
      {!won && culprit && <p className="gameover__culprit">{culprit}</p>}

      {damage.length > 0 && (
        <ul className="gameover__damage">
          {damage.map((word) => (
            <li key={word} className="gameover__damage-row">
              <span className="gameover__damage-word">{word}</span>
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="button button--primary button--big" onClick={game.rematch}>
        Rematch
      </button>
    </section>
  );
}


function Fatal({ title, detail }: { title: string; detail: string }) {
  return (
    <main className="fatal" role="alert">
      <div className="room__bg" aria-hidden="true" />
      <h1>{title}</h1>
      <p>{detail}</p>
    </main>
  );
}
